import {
  getSessions,
  createSession,
  renameSession as renameSessionApi,
  deleteSession as deleteSessionApi,
  type ShopifyChatSession,
} from "~/lib/network/shopify-chat";

interface ShopifyChatSessionsState {
  shopifyChatSessions: ShopifyChatSession[];
  currentShopifySessionId: string | null;
  shopifyChatConnectionId: string | null;
  fetchShopifyChatSessions: (connectionId: string) => Promise<void>;
  createShopifyChatSession: (connectionId: string, firstMessage?: string) => Promise<ShopifyChatSession>;
  selectShopifyChatSession: (sessionId: string | null) => void;
  renameShopifyChatSession: (sessionId: string, title: string) => Promise<void>;
  deleteShopifyChatSession: (sessionId: string) => Promise<void>;
  resetShopifyChat: () => void;
}

const defaultState = {
  shopifyChatSessions: [] as ShopifyChatSession[],
  currentShopifySessionId: null as string | null,
  shopifyChatConnectionId: null as string | null,
};

export const createShopifyChatSessionsSlice = (set: any, get: any, storeApi: any) => {
  return {
    ...defaultState,

    fetchShopifyChatSessions: async (connectionId: string) => {
      const sessions = await getSessions(connectionId);
      set({
        shopifyChatSessions: sessions,
        shopifyChatConnectionId: connectionId,
      });
      // Auto-select most recent session if none selected
      if (!get().currentShopifySessionId && sessions.length > 0) {
        set({ currentShopifySessionId: sessions[0].session_id });
      }
    },

    createShopifyChatSession: async (connectionId: string, firstMessage: string = "") => {
      const session = await createSession(connectionId, firstMessage);
      set((state: ShopifyChatSessionsState) => ({
        shopifyChatSessions: [session, ...state.shopifyChatSessions],
        currentShopifySessionId: session.session_id,
      }));
      return session;
    },

    selectShopifyChatSession: (sessionId: string | null) => {
      set({ currentShopifySessionId: sessionId });
    },

    renameShopifyChatSession: async (sessionId: string, title: string) => {
      const connectionId = get().shopifyChatConnectionId;
      if (!connectionId) return;

      await renameSessionApi(connectionId, sessionId, title);
      const updatedSessions = get().shopifyChatSessions.map(
        (session: ShopifyChatSession) => {
          if (session.session_id === sessionId) {
            return { ...session, title };
          }
          return session;
        }
      );
      set({ shopifyChatSessions: updatedSessions });
    },

    deleteShopifyChatSession: async (sessionId: string) => {
      const connectionId = get().shopifyChatConnectionId;
      if (!connectionId) return;

      await deleteSessionApi(connectionId, sessionId);
      const updatedSessions = get().shopifyChatSessions.filter(
        (session: ShopifyChatSession) => session.session_id !== sessionId
      );

      // Clear selection if we deleted the current session (hook will clear messages)
      const newCurrentId = get().currentShopifySessionId === sessionId ? null : get().currentShopifySessionId;

      set({
        shopifyChatSessions: updatedSessions,
        currentShopifySessionId: newCurrentId,
      });
    },

    resetShopifyChat: () => set(defaultState),
  };
};
